import { useState, FormEvent } from 'react';

export default function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    moveFrom: '',
    moveTo: '',
    moveDate: '',
    moveSize: '',
    services: [] as string[],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('moveFrom', formData.moveFrom);
      formBody.append('moveTo', formData.moveTo);
      formBody.append('moveDate', formData.moveDate);
      formBody.append('moveSize', formData.moveSize);
      formBody.append('services', formData.services.join(', '));
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6q66qma739gopmlh56g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          moveFrom: '',
          moveTo: '',
          moveDate: '',
          moveSize: '',
          services: [],
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service],
    }));
  };

  return (
    <section id="quote-form" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-bold mb-4">
            <i className="ri-file-list-3-line mr-2"></i>Free Estimate
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Get Your Personalized<br />Moving Quote Today
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Tell us about your move and we'll provide a detailed, transparent estimate within 24 hours. No hidden fees, no surprises.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl">
              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="space-y-6">
                  {/* Personal Info */}
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 text-amber-600 rounded-lg">
                        <i className="ri-user-line"></i>
                      </div>
                      Your Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                          placeholder="(760) 555-0123"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  {/* Move Details */}
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-sky-100 text-sky-600 rounded-lg">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      Move Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="moveFrom" className="block text-sm font-semibold text-slate-700 mb-2">
                          Moving From *
                        </label>
                        <input
                          type="text"
                          id="moveFrom"
                          name="moveFrom"
                          required
                          value={formData.moveFrom}
                          onChange={(e) => setFormData({ ...formData, moveFrom: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                          placeholder="City, State"
                        />
                      </div>
                      <div>
                        <label htmlFor="moveTo" className="block text-sm font-semibold text-slate-700 mb-2">
                          Moving To *
                        </label>
                        <input
                          type="text"
                          id="moveTo"
                          name="moveTo"
                          required
                          value={formData.moveTo}
                          onChange={(e) => setFormData({ ...formData, moveTo: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                          placeholder="City, State"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label htmlFor="moveSize" className="block text-sm font-semibold text-slate-700 mb-2">
                          Home Size *
                        </label>
                        <select
                          id="moveSize"
                          name="moveSize"
                          required
                          value={formData.moveSize}
                          onChange={(e) => setFormData({ ...formData, moveSize: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                        >
                          <option value="">Select size</option>
                          <option value="Studio">Studio</option>
                          <option value="1 Bedroom">1 Bedroom</option>
                          <option value="2 Bedrooms">2 Bedrooms</option>
                          <option value="3 Bedrooms">3 Bedrooms</option>
                          <option value="4+ Bedrooms">4+ Bedrooms</option>
                          <option value="Office">Office</option>
                          <option value="Commercial">Commercial</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="moveDate" className="block text-sm font-semibold text-slate-700 mb-2">
                          Preferred Move Date
                        </label>
                        <input
                          type="date"
                          id="moveDate"
                          name="moveDate"
                          value={formData.moveDate}
                          onChange={(e) => setFormData({ ...formData, moveDate: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Services */}
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-lg">
                        <i className="ri-checkbox-multiple-line"></i>
                      </div>
                      Services Needed
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {['Packing', 'Loading', 'Unloading', 'Unpacking', 'Furniture Assembly', 'Specialty Items'].map((service) => (
                        <label key={service} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            name="services"
                            value={service}
                            checked={formData.services.includes(service)}
                            onChange={() => handleServiceToggle(service)}
                            className="w-5 h-5 text-amber-500 border-2 border-slate-300 rounded focus:ring-amber-500 cursor-pointer"
                          />
                          <span className="text-base text-slate-700 group-hover:text-slate-900 transition-colors">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="pt-6 border-t border-slate-200">
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      maxLength={500}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all resize-none text-base"
                      placeholder="Tell us about any special requirements, large items, or questions you have..."
                    ></textarea>
                    <div className="text-sm text-slate-500 mt-2">{formData.message.length}/500 characters</div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending Your Request...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill mr-2"></i>
                        Get My Free Quote Now
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-emerald-50 border-2 border-emerald-200 text-emerald-800 px-5 py-4 rounded-xl flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-2xl flex-shrink-0"></i>
                      <div>
                        <div className="font-bold mb-1">Thank you for your request!</div>
                        <div className="text-sm">We'll contact you within 24 hours with your personalized quote.</div>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border-2 border-red-200 text-red-800 px-5 py-4 rounded-xl flex items-start gap-3">
                      <i className="ri-error-warning-fill text-2xl flex-shrink-0"></i>
                      <div>
                        <div className="font-bold mb-1">Something went wrong</div>
                        <div className="text-sm">Please try again or call us directly at (760) 625-7767</div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar - Takes 1 column */}
          <div className="space-y-6">
            {/* Why Request Quote Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-amber-400 text-2xl"></i>
                Why Request a Quote?
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-slate-200 text-sm leading-relaxed">Free, no-obligation estimates with transparent pricing</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-slate-200 text-sm leading-relaxed">Fast response within 24 hours guaranteed</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-slate-200 text-sm leading-relaxed">Licensed and insured professionals you can trust</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-slate-200 text-sm leading-relaxed">No hidden fees or surprise charges</span>
                </div>
              </div>
            </div>

            {/* Contact Info Card */}
            <div className="bg-amber-500 rounded-2xl p-6 text-white shadow-xl">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <i className="ri-phone-line text-2xl"></i>
                Prefer to Talk?
              </h3>
              <p className="text-amber-50 mb-4 text-sm leading-relaxed">
                Call us now for immediate assistance. Our team is ready to help!
              </p>
              <div className="space-y-3">
                <a href="tel:7606257767" className="flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-colors">
                  <i className="ri-phone-fill text-xl"></i>
                  <span className="font-bold">(760) 625-7767</span>
                </a>
                <a href="tel:5624553028" className="flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-colors">
                  <i className="ri-phone-fill text-xl"></i>
                  <span className="font-bold">(562) 455-3028</span>
                </a>
                <a href="tel:7603472727" className="flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-colors">
                  <i className="ri-phone-fill text-xl"></i>
                  <span className="font-bold">(760) 347-2727</span>
                </a>
              </div>
            </div>

            {/* Hours Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 text-white">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <i className="ri-time-line text-sky-400 text-2xl"></i>
                Business Hours
              </h3>
              <div className="flex items-center justify-between text-slate-200">
                <span className="font-semibold">Monday - Sunday</span>
                <span>9:00 AM - 5:00 PM</span>
              </div>
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <i className="ri-checkbox-circle-fill"></i>
                  <span>Same-day service available</span>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-6 border border-white/10 text-white text-center">
              <i className="ri-shield-star-line text-5xl text-amber-400 mb-3"></i>
              <div className="font-bold text-lg mb-2">Licensed & Insured</div>
              <div className="text-slate-300 text-sm">
                Your belongings are protected with full insurance coverage
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}